/*
  Product spec tables — one source of truth, two presentations.

  The backend emits axle specs in either shape:
    A) 2 tables x 3 columns   -> Felgendetails | Vorderachse | Hinterachse
    B) 4 tables x 2 columns   -> Felgendetails | Vorderachse   (+ Hinterachse, same for Reifendetails)

  Both are parsed into one model, then rendered as:
    desktop (>=750px) -> merged 3-column table per group, NO axle drawings
    mobile  (<750px)  -> one table per axle per group, WITH axle drawings

  On desktop, rows whose value is identical on both axles collapse into a single
  spanning cell; rows that actually differ are highlighted. A staggered setup then
  reads at a glance instead of hiding inside a wall of duplicated text.

  Only one variant is ever in the DOM (re-rendered on breakpoint change), so the
  spec text is never duplicated. Empty cells are detected here (same rule as
  global.js removeEmptyProductTableRows) because this script runs ahead of that
  handler: deferred scripts execute while readyState is "interactive".
  If this script never runs, the original tables are left untouched.
*/
(function () {
  var DESKTOP_MQ = '(min-width: 750px)';
  var TABLE_SELECTOR = [
    '.product__description table',
    '.product-description table',
    '.product__info-panel table',
    '.rte table'
  ].join(',');

  var MEANINGFUL = 'img, picture, video, iframe, svg, a, button, input, select, textarea';

  function norm(value) {
    return String(value == null ? '' : value)
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function textOf(el) {
    return String((el && el.textContent) || '').replace(/\s+/g, ' ').trim();
  }

  function escapeHtml(value) {
    var d = document.createElement('div');
    d.textContent = String(value == null ? '' : value);
    return d.innerHTML;
  }

  function isEmptyCell(cell) {
    if (!cell) return true;
    var text = String(cell.textContent || '').replace(/\u00a0/g, '').replace(/\s+/g, '').trim();
    if (text) return false;
    return !cell.querySelector(MEANINGFUL);
  }

  function hasAnyValue(row) {
    return row.front != null || row.rear != null;
  }

  /* global.js makeTablesResponsive() wraps every .rte table in a scroller. */
  function outerNode(table) {
    return table.closest('.responsive-table') || table;
  }

  function wrapTable(tableHtml) {
    return tableHtml ? '<div class="responsive-table">' + tableHtml + '</div>' : '';
  }

  /* Header cells may already carry an injected axle drawing; read the label only. */
  function headerLabel(cell) {
    var span = cell.querySelector('.rs-axle-table-heading__text');
    return textOf(span || cell);
  }

  function classify(table) {
    var firstRow = table.querySelector('tr');
    if (!firstRow) return null;

    var headers = [].slice.call(firstRow.querySelectorAll('th'));
    if (headers.length < 2) return null;

    var labels = headers.map(headerLabel);
    var lower = labels.map(norm);

    var group = null;
    if (lower[0].indexOf('felge') > -1) group = 'rim';
    else if (lower[0].indexOf('reifen') > -1) group = 'tire';
    if (!group) return null;

    var frontIdx = -1;
    var rearIdx = -1;
    for (var i = 1; i < lower.length; i++) {
      if (frontIdx < 0 && lower[i].indexOf('vorderachse') > -1) frontIdx = i;
      if (rearIdx < 0 && lower[i].indexOf('hinterachse') > -1) rearIdx = i;
    }
    if (frontIdx < 0 && rearIdx < 0) return null;

    return { table: table, group: group, title: labels[0], labels: labels, frontIdx: frontIdx, rearIdx: rearIdx };
  }

  function dataRows(table) {
    return [].slice.call(table.querySelectorAll('tr')).filter(function (row) {
      return !row.querySelector('th');
    });
  }

  /* Merge every spec table into { rim: group, tire: group } */
  function buildModel(specs) {
    var groups = {};

    specs.forEach(function (spec) {
      var g = groups[spec.group];
      if (!g) {
        g = groups[spec.group] = { title: spec.title, frontLabel: '', rearLabel: '', order: [], map: {} };
      }
      if (spec.frontIdx > -1 && !g.frontLabel) g.frontLabel = spec.labels[spec.frontIdx];
      if (spec.rearIdx > -1 && !g.rearLabel) g.rearLabel = spec.labels[spec.rearIdx];

      dataRows(spec.table).forEach(function (row) {
        var cells = [].slice.call(row.children);
        if (cells.length < 2) return;

        var key = textOf(cells[0]);
        if (!key) return;

        if (!g.map[key]) {
          g.map[key] = { labelHtml: cells[0].innerHTML, front: null, rear: null, frontText: '', rearText: '' };
          g.order.push(key);
        }
        if (spec.frontIdx > -1 && !isEmptyCell(cells[spec.frontIdx])) {
          g.map[key].front = cells[spec.frontIdx].innerHTML;
          g.map[key].frontText = textOf(cells[spec.frontIdx]);
        }
        if (spec.rearIdx > -1 && !isEmptyCell(cells[spec.rearIdx])) {
          g.map[key].rear = cells[spec.rearIdx].innerHTML;
          g.map[key].rearText = textOf(cells[spec.rearIdx]);
        }
      });
    });

    return groups;
  }

  function hasSide(g, side) {
    return g.order.some(function (key) {
      return g.map[key][side] != null;
    });
  }

  /* A gap on one axle stays blank; rows with no value on ANY axle are dropped. */
  function cellValue(v) {
    return v != null ? v : '';
  }

  /* ---------- desktop: symmetric 3-column grid, no drawings ---------- */
  function desktopTable(g) {
    var withRear = hasSide(g, 'rear');
    var keys = g.order.filter(function (key) { return hasAnyValue(g.map[key]); });

    var head = '<tr><th>' + escapeHtml(g.title) + '</th><th>' + escapeHtml(g.frontLabel || 'Vorderachse') + '</th>' +
      (withRear ? '<th>' + escapeHtml(g.rearLabel || 'Hinterachse') + '</th>' : '') + '</tr>';

    var body = keys.map(function (key) {
      var row = g.map[key];

      if (!withRear) {
        return '<tr><td>' + row.labelHtml + '</td><td>' + cellValue(row.front) + '</td></tr>';
      }

      /* Every row keeps all three cells so the column grid never breaks.
         Values repeated on both axles get the calm class; the few that really
         differ per axle carry the emphasis. */
      var cls = '';
      if (row.front != null && row.rear != null) {
        cls = norm(row.frontText) === norm(row.rearText) ? ' class="rs-row--same"' : ' class="rs-row--diff"';
      }

      return '<tr' + cls + '><td>' + row.labelHtml + '</td><td>' +
        cellValue(row.front) + '</td><td>' + cellValue(row.rear) + '</td></tr>';
    }).join('');

    return '<table class="rs-spec-group"><tbody>' + head + body + '</tbody></table>';
  }

  /* ---------- mobile: one table per axle, with drawings ---------- */
  function axleHeaderHtml(group, side, label) {
    var v = window.rsAxleVisuals || {};
    var src = group === 'rim'
      ? (side === 'front' ? v.rimFront : v.rimRear)
      : (side === 'front' ? v.tireFront : v.tireRear);
    var alt = group === 'rim'
      ? (side === 'front' ? v.rimFrontAlt : v.rimRearAlt)
      : (side === 'front' ? v.tireFrontAlt : v.tireRearAlt);

    var img = src
      ? '<img class="rs-axle-table-heading__image" src="' + escapeHtml(src) + '" alt="' + escapeHtml(alt || label) +
        '" loading="lazy" width="86" height="54">'
      : '';

    return '<span class="rs-axle-table-heading"><span class="rs-axle-table-heading__text">' +
      escapeHtml(label) + '</span>' + img + '</span>';
  }

  function mobileTable(g, group, side) {
    if (!hasSide(g, side)) return '';

    var label = (side === 'front' ? g.frontLabel : g.rearLabel) ||
      (side === 'front' ? 'Vorderachse' : 'Hinterachse');

    var body = g.order.filter(function (key) {
      return g.map[key][side] != null;
    }).map(function (key) {
      return '<tr><td>' + g.map[key].labelHtml + '</td><td>' + g.map[key][side] + '</td></tr>';
    }).join('');

    if (!body) return '';

    /* data-rs-visual-added stops global.js from injecting a second drawing */
    return '<table class="rs-spec-group"><tbody><tr><th>' + escapeHtml(g.title) +
      '</th><th data-rs-visual-added="true">' + axleHeaderHtml(group, side, label) + '</th></tr>' +
      body + '</tbody></table>';
  }

  function render(container, groups, isDesktop) {
    var order = ['rim', 'tire'];
    var html;

    if (isDesktop) {
      html = '<div class="rs-specs-desktop">' + order.map(function (k) {
        return groups[k] ? wrapTable(desktopTable(groups[k])) : '';
      }).join('') + '</div>';
    } else {
      var parts = [];
      ['front', 'rear'].forEach(function (side) {
        order.forEach(function (k) {
          if (groups[k]) parts.push(wrapTable(mobileTable(groups[k], k, side)));
        });
      });
      html = '<div class="rs-specs-mobile">' + parts.join('') + '</div>';
    }

    container.innerHTML = html;
  }

  function init() {
    if (document.querySelector('[data-rs-specs]')) return;

    var tables = [].slice.call(document.querySelectorAll(TABLE_SELECTOR)).filter(function (t, i, arr) {
      return arr.indexOf(t) === i && !t.closest('[data-rs-specs]');
    });

    var specs = tables.map(classify).filter(Boolean);
    if (!specs.length) return;

    var groups = buildModel(specs);

    /* Only reshape when the product really has both axles; otherwise leave the DOM alone. */
    var bothAxles = Object.keys(groups).some(function (k) {
      return hasSide(groups[k], 'front') && hasSide(groups[k], 'rear');
    });
    if (!bothAxles) return;

    var anchor = outerNode(specs[0].table);
    if (!anchor.parentNode) return;

    var container = document.createElement('div');
    container.className = 'rs-specs';
    container.setAttribute('data-rs-specs', '');
    anchor.parentNode.insertBefore(container, anchor);
    specs.forEach(function (spec) {
      var node = outerNode(spec.table);
      if (node.parentNode) node.remove();
    });

    var mq = window.matchMedia(DESKTOP_MQ);
    var paint = function () { render(container, groups, mq.matches); };
    paint();

    if (mq.addEventListener) mq.addEventListener('change', paint);
    else if (mq.addListener) mq.addListener(paint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
